/**
 * 🎯 OBJECTIF PÉDAGOGIQUE ACTIF - BANDEAU DYNAMIQUE
 * =================================================
 * 
 * Affiche l'objectif pédagogique en cours de travail.
 * Owen sélectionne automatiquement l'objectif à travailler.
 * L'utilisateur n'a AUCUN contrôle sur cette sélection.
 * 
 * Qualiopi-ready: Gouvernance pédagogique Owen exclusive.
 * 
 * 🔒 P0 FIX (03/01/2026): useSafeFetch + AbortController obligatoire
 * 🔒 P1.2 MOA (Jan 2026): Synchronisation avec événements d'évaluation
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Target, CheckCircle2, TrendingUp, BookOpen, Loader2 } from 'lucide-react';
import { Progress } from './ui/progress';
import { getBackendUrl } from '../config/environment';
import useSafeFetch from '../hooks/useSafeFetch';
import { subscribeToEvaluation, subscribeToRefresh } from '../utils/pedagogicalEvents';

const BACKEND_URL = getBackendUrl();

/**
 * ActiveObjectiveBanner - Bandeau fixe affichant l'objectif en cours
 * 
 * @param {number} moduleId - ID du module
 * @param {string} moduleName - Nom du module (P1.5-B)
 * @param {boolean} isSessionActive - Session vocale active (P1.5-A: gel du refresh)
 * @param {function} onObjectiveLoaded - Callback quand l'objectif est chargé
 */
const ActiveObjectiveBanner = ({ moduleId, moduleName, isSessionActive = false, onObjectiveLoaded }) => {
  const [objective, setObjective] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [moduleCompleted, setModuleCompleted] = useState(false);
  
  // 🔒 P1.5-A MOA: Flag pour savoir si l'objectif a été chargé au moins une fois
  const hasLoadedOnceRef = useRef(false);
  
  // 🔒 FIX BOUCLE INFINIE: Stocker le callback dans un ref pour éviter
  // que useCallback ne se re-crée à chaque changement de référence du callback parent
  const onObjectiveLoadedRef = useRef(onObjectiveLoaded);
  useEffect(() => {
    onObjectiveLoadedRef.current = onObjectiveLoaded;
  }, [onObjectiveLoaded]);
  
  // 🔒 P0 FIX: useSafeFetch avec AbortController automatique
  const { safeFetch, isAborted } = useSafeFetch();
  const isMountedRef = useRef(true);

  // Cleanup au démontage
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // 🔒 P1.5-A MOA: Fonction de rechargement avec protection session active
  const fetchActiveObjective = useCallback(async (showLoader = true, forceRefresh = false) => {
    // 🔒 P1.5-A MOA: GEL PENDANT SESSION ACTIVE
    // Si une session est active ET qu'on a déjà chargé une fois, ne pas re-fetcher
    if (isSessionActive && hasLoadedOnceRef.current && !forceRefresh) {
      console.log('[ActiveObjectiveBanner] 🔒 P1.5-A: Session active - refresh BLOQUÉ (objectif gelé)');
      return;
    }
    
    try {
      if (showLoader) setLoading(true);
      setError(null);
      
      const token = localStorage.getItem('token');
      const timestamp = Date.now();
      
      console.log(`[ActiveObjectiveBanner] 🔄 [${timestamp}] Rechargement objectif module ${moduleId}...`);
      
      // 🔒 P0 FIX: Use safeFetch (native fetch + AbortController + single body read)
      // 🔒 P1.2 MOA: Cache-bust pour forcer données fraîches après évaluation
      const data = await safeFetch(`${BACKEND_URL}/api/pedagogical/scenario/current/${moduleId}?_t=${timestamp}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache'
        },
        timeout: 15000
      });
      
      // 🔒 Guard: Ne pas mettre à jour si composant démonté
      if (!isMountedRef.current) return;
      
      if (data.module_completed) {
        setModuleCompleted(true);
        setObjective({
          title: "Module terminé ✓",
          description: "Tous les objectifs ont été validés par Owen.",
          progress: 100
        });
      } else if (data.has_active_objective) {
        // 🔒 GATE 3 AUDIT (30/12/2024): ZÉRO FALLBACK SILENCIEUX
        if (!data.evidences_needed && data.evidences_needed !== 0) {
          console.error('[ActiveObjectiveBanner] ⚠️ ERREUR: evidences_needed non fourni par API');
        }
        
        // 🔒 P1.2 MOA AUDIT: Log de la progression reçue
        console.log(`[ActiveObjectiveBanner] 📊 P1.5-A: Progression reçue: ${data.progress}% (${data.evidences_collected}/${data.evidences_needed} preuves)`);
        
        setObjective({
          id: data.objective_id,
          title: data.objective_title,
          description: data.objective_description,
          progress: data.progress || 0,
          evidencesCollected: data.evidences_collected || 0,
          evidencesNeeded: data.evidences_needed,
          criteria: data.evaluation_criteria || []
        });
      }
      
      // 🔒 P1.5-A MOA: Marquer qu'on a chargé au moins une fois
      hasLoadedOnceRef.current = true;
      
      // Callback pour le parent (via ref pour stabilité)
      if (onObjectiveLoadedRef.current) {
        onObjectiveLoadedRef.current(data);
      }
      
    } catch (err) {
      // 🔒 P0 FIX: Ignorer les erreurs d'abort (navigation)
      if (isAborted(err)) {
        console.log('[ActiveObjectiveBanner] Request aborted (navigation)');
        return;
      }
      
      if (!isMountedRef.current) return;
      
      console.error('[ActiveObjectiveBanner] Erreur:', err);
      setError(err.message);
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
  }, [moduleId, isSessionActive, safeFetch, isAborted]);

  // Chargement initial
  useEffect(() => {
    if (moduleId) {
      fetchActiveObjective(true);
    }
  }, [moduleId, fetchActiveObjective]);

  // 🔒 P1.5-A MOA: Écouter les événements d'évaluation Owen (avec protection session active)
  // 🔒 P1.5-D MOA: Permettre la synchro de progression finale APRÈS feedback
  useEffect(() => {
    console.log(`[ActiveObjectiveBanner] 👂 P1.5-A/D: Abonnement événements module ${moduleId} (session active: ${isSessionActive})`);
    
    // S'abonner aux événements d'évaluation
    const unsubscribeEval = subscribeToEvaluation((data) => {
      console.log(`[ActiveObjectiveBanner] 📡 P1.5-A/D: Événement evaluation_complete reçu:`, data);
      
      // 🔒 P1.5-D MOA: AUTORISER la mise à jour si c'est une synchro de fin de session
      // source === 'p15d_sync' signifie que le feedback a été généré et que la progression doit être mise à jour
      if (data.source === 'p15d_sync' && data.progress_updated) {
        console.log(`[ActiveObjectiveBanner] 🔒 P1.5-D → UX P1 (12/02/2026): Synchro fin de session - REFETCH COMPLET depuis source unique`);
        //
        // 🔒 UX P1 FIX — aucun patch local partiel.
        //    Avant ce fix, on ne mettait à jour QUE 'progress' via setObjective local,
        //    ce qui laissait 'evidencesCollected' figé (désynchronisation % ↔ preuves
        //    visibles à l'utilisateur). Exigence COMEX : single source of truth.
        fetchActiveObjective(false, /* forceRefresh */ true);
        return;
      }
      
      // 🔒 P1.5-A MOA: NE PAS RAFRAÎCHIR SI SESSION ACTIVE (sauf synchro P1.5-D ci-dessus)
      // L'objectif reste FIGÉ pendant toute la durée de la session vocale
      if (isSessionActive) {
        console.log(`[ActiveObjectiveBanner] 🔒 P1.5-A: Session active - événement IGNORÉ (objectif gelé)`);
        return;
      }
      
      // Recharger si c'est notre module ou si pas de module spécifié
      if (!data.module_id || data.module_id === parseInt(moduleId)) {
        console.log(`[ActiveObjectiveBanner] 🔄 P1.5-A: Déclenchement refresh après évaluation...`);
        fetchActiveObjective(false);
      }
    });

    // S'abonner aux événements de refresh manuel (aussi protégé)
    const unsubscribeRefresh = subscribeToRefresh((data) => {
      console.log(`[ActiveObjectiveBanner] 📡 P1.5-A: Événement refresh reçu:`, data);
      
      // 🔒 P1.5-A MOA: Même protection pour le refresh manuel
      if (isSessionActive) {
        console.log(`[ActiveObjectiveBanner] 🔒 P1.5-A: Session active - refresh manuel IGNORÉ`);
        return;
      }
      
      if (!data.moduleId || data.moduleId === parseInt(moduleId)) {
        fetchActiveObjective(false);
      }
    });

    // Cleanup au démontage
    return () => {
      console.log(`[ActiveObjectiveBanner] 🔕 P1.5-A/D: Désabonnement événements module ${moduleId}`);
      unsubscribeEval();
      unsubscribeRefresh();
    };
  }, [moduleId, isSessionActive, fetchActiveObjective]);

  // État de chargement
  if (loading) {
    return (
      <div className="bg-gradient-to-r from-purple-600/10 to-indigo-600/10 border-b border-purple-200/50 px-4 py-3">
        <div className="max-w-4xl mx-auto flex items-center gap-3">
          <Loader2 className="w-5 h-5 text-purple-600 animate-spin" />
          <span className="text-sm text-purple-700">Chargement de l&apos;objectif pédagogique...</span>
        </div>
      </div>
    );
  }

  // Erreur
  if (error) {
    return null; // Masquer en cas d'erreur
  }

  // Pas d'objectif
  if (!objective) {
    return null;
  }

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -20 }}
        className={`${
          moduleCompleted 
            ? 'bg-gradient-to-r from-green-500/10 to-emerald-500/10 border-b border-green-200/50'
            : 'bg-gradient-to-r from-purple-600/10 to-indigo-600/10 border-b border-purple-200/50'
        } px-4 py-3 sticky top-0 z-40`}
      >
        <div className="max-w-4xl mx-auto">
          {/* 🔒 P1.5-B MOA: Header complet [Module — Objectif — %] */}
          <div className="flex items-center gap-3 mb-2">
            <div className={`p-2 rounded-lg ${
              moduleCompleted 
                ? 'bg-green-100' 
                : 'bg-purple-100'
            }`}>
              {moduleCompleted ? (
                <CheckCircle2 className="w-5 h-5 text-green-600" />
              ) : (
                <Target className="w-5 h-5 text-purple-600" />
              )}
            </div>
            
            <div className="flex-1">
              {/* 🔒 P1.5-B MOA: Affichage structuré [Module] — [Objectif] — [%] */}
              <div className="flex items-center gap-2 flex-wrap">
                {/* Nom du module (P1.5-B) */}
                {moduleName && (
                  <>
                    <span className="text-xs font-medium text-gray-600 bg-gray-100 px-2 py-0.5 rounded">
                      📚 {moduleName}
                    </span>
                    <span className="text-gray-400">—</span>
                  </>
                )}
                <span className={`text-xs font-semibold uppercase tracking-wide ${
                  moduleCompleted ? 'text-green-600' : 'text-purple-600'
                }`}>
                  🎯 Objectif en cours
                </span>
                {!moduleCompleted && objective.progress > 0 && (
                  <>
                    <span className="text-gray-400">—</span>
                    <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full font-bold">
                      {Math.round(objective.progress)}%
                    </span>
                  </>
                )}
              </div>
              
              <h3 className={`font-semibold text-base ${
                moduleCompleted ? 'text-green-800' : 'text-purple-900'
              }`}>
                {objective.title}
              </h3>
            </div>
          </div>

          {/* Barre de progression (si pas terminé) */}
          {!moduleCompleted && (
            <div className="flex items-center gap-3">
              <div className="flex-1">
                <Progress 
                  value={objective.progress} 
                  className="h-2 bg-purple-100"
                />
              </div>
              
              {/* 🔒 GATE 3 AUDIT (30/12/2024): ZÉRO FALLBACK - Affiche STRICTEMENT les valeurs backend */}
              <div className="flex items-center gap-2 text-xs text-purple-600">
                <BookOpen className="w-4 h-4" />
                <span>
                  📚 Preuves collectées : {objective.evidencesCollected} / {objective.evidencesNeeded ?? '?'}
                </span>
              </div>
            </div>
          )}

          {/* Description courte (optionnel) */}
          {objective.description && !moduleCompleted && (
            <p className="text-xs text-purple-600/80 mt-1 line-clamp-1">
              {objective.description}
            </p>
          )}
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default ActiveObjectiveBanner;
